import { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, Loader2, AlertCircle, FileImage, FileText } from 'lucide-react';
import { useState } from 'react';
import { parseResume, parseResumeImage } from '../lib/api';
import { extractTextFromPdf } from '../lib/pdf-extract';
import type { ResumeData } from '../types/resume';

interface Props {
  onParsed: (data: ResumeData) => void;
}

function isEmptyResume(data: ResumeData): boolean {
  const p = data.personal;
  const hasPersonal = p.fullName || p.email || p.phone;
  const hasExperience = data.experience.length > 0 && data.experience.some(e => e.company || e.title);
  const hasEducation = data.education.length > 0 && data.education.some(e => e.school);
  return !hasPersonal && !hasExperience && !hasEducation && data.skills.length === 0;
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result as string;
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function UploadZone({ onParsed }: Props) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploadType, setUploadType] = useState<'pdf' | 'image'>('pdf');

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;
    setLoading(true);
    setError('');
    
    try {
      const isImage = file.type.startsWith('image/');
      const isPdf = file.type === 'application/pdf';
      
      if (isImage) {
        // Handle image upload (screenshot/photo of resume)
        console.log('Processing image upload...');
        const base64Image = await fileToBase64(file);
        console.log(`Image converted to base64 (${base64Image.length} chars)`);
        
        const result = await parseResumeImage(base64Image);
        
        if (!result.data) {
          throw new Error('No data returned from image parser');
        }
        
        if (isEmptyResume(result.data)) {
          throw new Error(
            'Could not extract resume details from the image. Make sure the image is clear and the text is readable. Try uploading a PDF instead, or use "Create New Resume".'
          );
        }
        
        onParsed(result.data);
      } else if (isPdf) {
        // Handle PDF upload
        console.log('Extracting text from PDF...');
        const extractedText = await extractTextFromPdf(file);
        console.log(`Extracted ${extractedText.length} characters`);
        
        if (extractedText.length < 50) {
          throw new Error(
            'Could not extract text from PDF. The PDF may be scanned/image-based, password-protected, or use an unsupported format. Try uploading a screenshot of the resume instead, or use "Create New Resume".'
          );
        }
        
        console.log('Sending to AI parser...');
        const result = await parseResume(extractedText);
        
        if (!result.data) {
          throw new Error('No data returned from parser');
        }
        
        if (isEmptyResume(result.data)) {
          throw new Error(
            'Could not extract resume details. The PDF may be scanned/image-based, password-protected, or use an unsupported format. Try uploading a screenshot of the resume instead, or use "Create New Resume".'
          );
        }
        
        onParsed(result.data);
      } else {
        throw new Error('Unsupported file type. Please upload a PDF or image file (PNG, JPG, JPEG).');
      }
    } catch (err: any) {
      console.error('Parse error:', err);
      let errorMsg = err.message || 'Failed to parse resume';
      if (err.detail) {
        errorMsg += `\n\nDetails: ${err.detail}`;
      }
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [onParsed]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/webp': ['.webp'],
    },
    multiple: false,
  });

  return (
    <div className="w-full">
      {/* Upload type toggle */}
      <div className="flex gap-2 mb-3">
        <button
          type="button"
          onClick={() => setUploadType('pdf')}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            uploadType === 'pdf'
              ? 'bg-primary-100 text-primary-700'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
        >
          <FileText className="h-4 w-4" />
          PDF / Word / TXT
        </button>
        <button
          type="button"
          onClick={() => setUploadType('image')}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            uploadType === 'image'
              ? 'bg-primary-100 text-primary-700'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
        >
          <FileImage className="h-4 w-4" />
          Screenshot / Photo
        </button>
      </div>

      <div
        {...getRootProps()}
        className={`w-full border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${
          isDragActive
            ? 'border-primary-500 bg-primary-50'
            : error 
              ? 'border-red-300 bg-red-50'
              : 'border-slate-200 hover:border-slate-300'
        }`}
      >
        <input {...getInputProps()} />
        {loading ? (
          <div className="flex items-center justify-center gap-2 text-slate-500">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span className="text-sm">AI is reading your resume...</span>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            {uploadType === 'image' ? (
              <FileImage className="h-5 w-5 text-slate-400" />
            ) : (
              <Upload className="h-5 w-5 text-slate-400" />
            )}
            <p className="text-sm text-slate-500">
              {isDragActive 
                ? 'Drop file here' 
                : uploadType === 'image'
                  ? 'Drag & drop or click to upload screenshot/photo'
                  : 'Drag & drop or click to upload PDF'
              }
            </p>
            <p className="text-xs text-slate-400">
              {uploadType === 'image' 
                ? 'PNG, JPG, JPEG, WEBP supported'
                : 'PDF, DOC, DOCX, TXT supported'
              }
            </p>
            {uploadType === 'image' && (
              <p className="text-xs text-primary-600 font-medium">
                💡 Tip: Screenshot uploads often work better for complex resume layouts!
              </p>
            )}
          </div>
        )}
        {error && (
          <div className="mt-3 flex items-start gap-2 text-left">
            <AlertCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
            <p className="text-xs text-red-600 leading-relaxed whitespace-pre-line">{error}</p>
          </div>
        )}
      </div>
    </div>
  );
}
